// TanStack Query client for the portal (constitution §2: no hand-rolled fetch caching).
// A 401 on any query, or on a mutation not marked as part of sign-in, means the session ended
// (idle 1 h, absolute 8 h, revoked): the app is told once so it can drop data and go to /login.
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '@cis/api-client';

const HTTP_UNAUTHORIZED = 401;
const HTTP_SERVER_ERROR_MIN = 500;
const MAX_RETRIES = 2;
const LIST_STALE_MS = 30_000; // plan §9: lists are fresh for 30 s

export interface QueryClientOptions {
  /** Called when the API says the session is gone. */
  readonly onUnauthenticated?: () => void;
  /** Retries for network and 5xx failures. Tests pass 0. */
  readonly retries?: number;
}

/** Marks a mutation whose 401 is handled by the caller (the sign-in flow), not by the app. */
export const OWN_AUTH_ERRORS = { ownAuthErrors: true } as const;

export function isUnauthenticated(err: unknown): boolean {
  return err instanceof ApiError && err.status === HTTP_UNAUTHORIZED;
}

// 4xx answers will not change on retry; network drops and 5xx might.
function retryable(err: unknown): boolean {
  if (err instanceof NetworkError) return true;
  return err instanceof ApiError && err.status >= HTTP_SERVER_ERROR_MIN;
}

export function createQueryClient(opts: QueryClientOptions = {}): QueryClient {
  const maxRetries = opts.retries ?? MAX_RETRIES;
  const signedOut = (err: unknown): void => {
    if (isUnauthenticated(err)) opts.onUnauthenticated?.();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError: signedOut }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        if (mutation.meta?.['ownAuthErrors'] !== true) signedOut(err);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: LIST_STALE_MS,
        retry: (count, err) => count < maxRetries && retryable(err),
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}
